import { useEffect, useState, useRef } from "react";
import {
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  getDocFromServer,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";

interface StreakData {
  currentStreak: number;
  lastActiveDate: string | null;
  activeDates?: string[];
}

function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function daysBetween(a: string, b: string): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round(
    (new Date(b).getTime() - new Date(a).getTime()) / msPerDay
  );
}

export function useStreak() {
  const { user } = useAuth();
  const [currentStreak, setCurrentStreak] = useState<number>(0);
  const [activeDates, setActiveDates] = useState<string[]>([]);

  const [isFirstVisit, setIsFirstVisit] = useState<boolean | null>(() => {
    if (!user?.uid) return null;
    const sessionKey = `bodyown_greeting_lock_${user.uid}`;
    const locked = sessionStorage.getItem(sessionKey);
    if (locked === "welcome") return true;
    if (locked === "back") return false;
    return null;
  });

  const [loading, setLoading] = useState(true);

  const syncedUidRef = useRef<string | null>(null);
  const listenerRetriedRef = useRef(false);
  const syncRetriedRef = useRef(false);
  const uid = user?.uid ?? null;

  useEffect(() => {
    if (!uid) {
      syncedUidRef.current = null;
      setCurrentStreak(0);
      setActiveDates([]);
      setIsFirstVisit(null);
      setLoading(false);
      return;
    }

    // Reset retry guards whenever uid changes
    listenerRetriedRef.current = false;
    syncRetriedRef.current = false;

    const streakRef = doc(db, "streaks", uid);
    const userRef = doc(db, "users", uid);

    let unsubscribe: () => void;

    // 1. SETUP REAL-TIME LISTENER (with one-shot retry on permission-denied)
    const subscribe = () => {
      unsubscribe = onSnapshot(
        streakRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data() as StreakData;
            setCurrentStreak(data.currentStreak);
            setActiveDates(data.activeDates ?? []);
          }
          setLoading(false);
        },
        (err) => {
          if (err.code === "permission-denied" && !listenerRetriedRef.current) {
            listenerRetriedRef.current = true;
            // Token not yet propagated — retry once after a short delay
            setTimeout(() => {
              unsubscribe?.();
              subscribe();
            }, 1500);
          } else {
            console.error("Streak listener error:", err);
            setLoading(false);
          }
        }
      );
    };

    subscribe();

    // 2. SYNC LOGIC (runs once per session, with one-shot retry on permission-denied)
    const syncStreak = async (isRetry = false) => {
      if (syncedUidRef.current === uid) return;
      if (sessionStorage.getItem(`bodyown_account_deleted_${uid}`)) return;

      try {
        const [streakSnap, userSnap] = await Promise.all([
          getDocFromServer(streakRef),
          getDocFromServer(userRef),
        ]);

        const today = toISODate(new Date());
        const sessionGreetingKey = `bodyown_greeting_lock_${uid}`;
        const lockedGreeting = sessionStorage.getItem(sessionGreetingKey);

        const userData = userSnap.data();
        const dbHasVisited = userData?.hasVisitedDashboard === true;

        if (dbHasVisited) {
          setIsFirstVisit(false);
          sessionStorage.setItem(sessionGreetingKey, "back");
        } else if (lockedGreeting === "welcome") {
          setIsFirstVisit(true);
        } else {
          setIsFirstVisit(true);
          sessionStorage.setItem(sessionGreetingKey, "welcome");

          if (userData?.ftuCompleted === true) {
            updateDoc(userRef, {
              hasVisitedDashboard: true,
              updatedAt: serverTimestamp(),
            }).catch((e) => console.error("Failed to update visited flag", e));
          }
        }

        let newStreak = 1;
        let prevDates: string[] = [];

        if (streakSnap.exists()) {
          const data = streakSnap.data() as StreakData;
          const last = data.lastActiveDate;
          prevDates = data.activeDates ?? [];

          if (last === today) {
            syncedUidRef.current = uid;
            return;
          } else {
            const diff = last ? daysBetween(last, today) : null;
            newStreak = diff === 1 ? data.currentStreak + 1 : 1;
          }
        }

        const updatedActiveDates = prevDates.includes(today)
          ? prevDates
          : [today, ...prevDates].slice(0, 30);

        await setDoc(streakRef, {
          currentStreak: newStreak,
          lastActiveDate: today,
          activeDates: updatedActiveDates,
          updatedAt: serverTimestamp(),
        });

        syncedUidRef.current = uid;
      } catch (err: unknown) {
        const isPermissionDenied =
          typeof err === "object" &&
          err !== null &&
          "code" in err &&
          (err as { code: string }).code === "permission-denied";

        if (isPermissionDenied && !isRetry && !syncRetriedRef.current) {
          syncRetriedRef.current = true;
          setTimeout(() => syncStreak(true), 1500);
        } else {
          console.error("Error syncing streak:", err);
        }
      }
    };

    syncStreak();

    return () => unsubscribe?.();
  }, [uid]);

  return { currentStreak, activeDates, isFirstVisit, loading };
}